import {useNavigate, useLocation} from 'react-router-dom'
import {useContext, useState} from 'react'
import {useQueryClient} from 'react-query'
import PostComposer from 'components/PostComposer'
import {getItemFromStorage} from 'utils/storage'
import {TabsContext} from 'contexts/TabsContext'

/**
 * Mobile bottom-tab nav — five equal slots, symmetric around the raised
 * Post action: Feed · Discover · [Post] · Map · Messages.
 *
 * Messages took the old Settings slot when Circles shipped (2026-08);
 * Settings stays reachable from the avatar menu in the top Navbar, which
 * already carries a Settings shortcut.
 *
 * Mounted ONCE at the app shell (pages/App) for every route outside its
 * exclusion list — pages must not mount it themselves. Persisting across
 * detail pages (profiles, jobs, classes, spaces) is deliberate: the tabs
 * are stable chrome, not a per-page feature.
 *
 * All icons are inline stroke SVGs inheriting currentColor so the
 * active/inactive tint is uniform (the old feed/map image assets had
 * baked-in colors and never reflected state). Post/Discover/Profile are
 * auth-only; logged-out visitors on public pages see Feed and Map, which
 * route them to the login wall.
 *
 * Hidden on lg+ (desktop has the top Navbar).
 */
const ICON_PATHS = {
  feed: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="7" y1="9" x2="17" y2="9" />
      <line x1="7" y1="13" x2="17" y2="13" />
      <line x1="7" y1="17" x2="13" y2="17" />
    </>
  ),
  map: (
    <>
      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  ),
  discover: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polygon points="15.5,8.5 13.5,13.5 8.5,15.5 10.5,10.5" />
    </>
  ),
  messages: (
    <path d="M21 11.5a8.38 8.38 0 01-8.4 8.4 8.5 8.5 0 01-3.8-.9L3 21l2-5.8a8.38 8.38 0 01-.9-3.7 8.5 8.5 0 018.5-8.5 8.38 8.38 0 018.4 8.5z" />
  ),
}

const TabIcon = ({name}) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICON_PATHS[name]}
  </svg>
)

const HomeNavigation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const {unreadChats} = useContext(TabsContext)
  const [isComposerOpen, setIsComposerOpen] = useState(false)
  const isAuth = Boolean(getItemFromStorage({name: 'user'})?.id)

  const isActive = (path) => location.pathname.startsWith(path)

  const Tab = ({path, icon, label, badge}) => (
    <button
      type="button"
      onClick={() => navigate(path)}
      className={`flex flex-1 flex-col items-center gap-y-0.5 py-2.5 cursor-pointer ${
        isActive(path)
          ? 'text-ash-pink font-bold'
          : 'text-regular-black font-medium'
      }`}
      aria-label={label}
      aria-current={isActive(path) ? 'page' : undefined}
    >
      <span className="relative">
        <TabIcon name={icon} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2 bg-sharpenn-coral text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      <span className="text-[11px]">{label}</span>
    </button>
  )

  return (
    <>
      <div className="lg:hidden z-10 fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="flex items-center w-full">
          <Tab path="/homepage" icon="feed" label="Feed" />
          {/* Discover is auth-only; the empty slot keeps Feed/Map from
              drifting for logged-out visitors. */}
          {isAuth ? (
            <Tab path="/discover" icon="discover" label="Discover" />
          ) : (
            <div className="flex-1" />
          )}

          {/* Central Post button — primary action, vertically centered
              within the bar so it reads as part of it rather than
              floating above. Auth-only. The slot stays reserved when
              logged out so the remaining tabs keep symmetric positions. */}
          <div className="flex flex-1 items-center justify-center self-stretch">
            {isAuth && (
              <button
                type="button"
                onClick={() => setIsComposerOpen(true)}
                className="flex items-center justify-center cursor-pointer"
                aria-label="Create a post"
              >
                <div className="w-11 h-11 rounded-full bg-sharpenn-coral hover:bg-sharpenn-rose transition-colors flex items-center justify-center shadow-small">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-white"
                    aria-hidden="true"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
              </button>
            )}
          </div>

          <Tab path="/map" icon="map" label="Map" />
          {/* Inbox (DMs, Circles, job applications). Auth-only; the reserved slot keeps
              the other tabs symmetric for logged-out visitors. */}
          {isAuth ? (
            <Tab
              path="/messages"
              icon="messages"
              label="Inbox"
              badge={unreadChats}
            />
          ) : (
            <div className="flex-1" />
          )}
        </div>
      </div>

      <PostComposer
        isOpen={isComposerOpen}
        onClose={() => setIsComposerOpen(false)}
        onPosted={async () => {
          // Cancel any in-flight feed fetch first: react-query v3 dedupes
          // refetches against in-flight requests, so without the cancel a
          // post created during a fetch would never trigger a fresh one.
          await queryClient.cancelQueries('chronologicalFeed')
          queryClient.invalidateQueries('chronologicalFeed')
        }}
      />
    </>
  )
}

export default HomeNavigation
